'use client'

import * as React from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, type UseFormSetError } from 'react-hook-form'
import type { ContactSubmissionInput } from '@/lib/forms/schemas'
import { contactSubmissionSchema } from '@/lib/forms/schemas'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { FormStatus } from '@/components/forms/form-status'

type ApiResponse =
  | { ok: true; message: string; warning?: string; fieldErrors?: Record<string, string[]> }
  | { ok: false; message: string; fieldErrors?: Record<string, string[]> }

const budgetOptions = [
  { value: '', label: 'Select a budget' },
  { value: 'under-10k', label: 'Under $10k' },
  { value: '10k-25k', label: '$10k - $25k' },
  { value: '25k-50k', label: '$25k - $50k' },
  { value: '50k-plus', label: '$50k+' },
  { value: 'unsure', label: 'Not sure yet' },
]

const topicOptions = [
  { value: 'general', label: 'General question' },
  { value: 'sales', label: 'Sales / pricing' },
  { value: 'support', label: 'Support request' },
  { value: 'partnership', label: 'Partnership' },
]

function setServerErrors(
  fieldErrors: Record<string, string[]> | undefined,
  setError: UseFormSetError<ContactSubmissionInput>,
) {
  if (!fieldErrors) return

  for (const [name, messages] of Object.entries(fieldErrors)) {
    if (!messages.length) continue
    const fieldName = name as keyof ContactSubmissionInput
    setError(fieldName, { type: 'server', message: messages[0] })
  }
}

export function ContactForm() {
  const [startedAt] = React.useState(() => new Date().toISOString())
  const [status, setStatus] = React.useState<{ kind: 'idle' | 'success' | 'error'; title?: string; description?: string }>({ kind: 'idle' })

  const form = useForm<ContactSubmissionInput>({
    resolver: zodResolver(contactSubmissionSchema),
    defaultValues: {
      name: '',
      email: '',
      company: '',
      topic: 'general',
      budget: undefined,
      message: '',
      newsletterOptIn: false,
      honeypot: '',
      startedAt,
      source: 'contact-page',
    },
    mode: 'onBlur',
  })

  const onSubmit = form.handleSubmit(async values => {
    setStatus({ kind: 'idle' })
    form.clearErrors()

    const response = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(values),
    })

    const data = (await response.json().catch(() => null)) as ApiResponse | null

    if (!response.ok || !data || !data.ok) {
      setServerErrors(data && !data.ok ? data.fieldErrors : undefined, form.setError)
      setStatus({
        kind: 'error',
        title: 'We could not send your message',
        description: data?.message ?? 'Please check the form and try again.',
      })
      return
    }

    form.reset({
      name: '',
      email: '',
      company: '',
      topic: 'general',
      budget: undefined,
      message: '',
      newsletterOptIn: false,
      honeypot: '',
      startedAt: new Date().toISOString(),
      source: 'contact-page',
    })

    setStatus({
      kind: 'success',
      title: 'Message received',
      description: data.warning ?? data.message,
    })
  })

  return (
    <form className="grid gap-6" onSubmit={onSubmit}>
      <input type="hidden" {...form.register('honeypot')} aria-hidden="true" tabIndex={-1} autoComplete="off" />
      <input type="hidden" {...form.register('startedAt')} />
      <input type="hidden" {...form.register('source')} />

      <FormStatus kind={status.kind} title={status.title} description={status.description} />

      <div className="grid gap-4 md:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor="name">Name</Label>
          <Input id="name" autoComplete="name" placeholder="Your name" {...form.register('name')} />
          {form.formState.errors.name ? <p className="text-sm text-destructive">{form.formState.errors.name.message}</p> : null}
        </div>
        <div className="grid gap-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" placeholder="you@example.com" {...form.register('email')} />
          {form.formState.errors.email ? <p className="text-sm text-destructive">{form.formState.errors.email.message}</p> : null}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor="company">Company</Label>
          <Input
            id="company"
            autoComplete="organization"
            placeholder="Optional"
            {...form.register('company', {
              setValueAs: value => (typeof value === 'string' && value.trim().length > 0 ? value : undefined),
            })}
          />
          {form.formState.errors.company ? <p className="text-sm text-destructive">{form.formState.errors.company.message}</p> : null}
        </div>
        <div className="grid gap-2">
          <Label htmlFor="topic">Topic</Label>
          <Select id="topic" {...form.register('topic')}>
            {topicOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          {form.formState.errors.topic ? <p className="text-sm text-destructive">{form.formState.errors.topic.message}</p> : null}
        </div>
      </div>

      <div className="grid gap-2 md:max-w-xs">
        <Label htmlFor="budget">Budget</Label>
        <Select
          id="budget"
          {...form.register('budget', {
            setValueAs: value => (typeof value === 'string' && value.trim().length > 0 ? value : undefined),
          })}
        >
          {budgetOptions.map(option => (
            <option key={option.value || 'empty'} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        {form.formState.errors.budget ? <p className="text-sm text-destructive">{form.formState.errors.budget.message}</p> : null}
      </div>

      <div className="grid gap-2">
        <Label htmlFor="message">Message</Label>
        <Textarea id="message" rows={6} placeholder="Tell us what you want to build or improve." {...form.register('message')} />
        {form.formState.errors.message ? <p className="text-sm text-destructive">{form.formState.errors.message.message}</p> : null}
      </div>

      <label className="flex items-start gap-3 rounded-xl border border-border bg-muted/30 p-4 text-sm">
        <input type="checkbox" className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-ring" {...form.register('newsletterOptIn')} />
        <span className="leading-6 text-muted-foreground">
          Send me occasional product updates and release notes.
        </span>
      </label>

      <div className="flex flex-col gap-3 sm:flex-row">
        <Button type="submit" disabled={form.formState.isSubmitting} className="sm:min-w-44">
          {form.formState.isSubmitting ? 'Sending...' : 'Send message'}
        </Button>
        <p className="text-sm leading-6 text-muted-foreground">
          We store the submission even if the notification step is delayed.
        </p>
      </div>
    </form>
  )
}
